<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { onKeyStroke, unrefElement, useElementBounding, useWindowSize } from '@vueuse/core'
import { computed, watch } from '#imports'

export interface UIPopoverProps {
    activator?: HTMLElement | ComponentPublicInstance | null
    width?: string
    ariaLabel?: string
    offset?: number
    viewportPadding?: number
}

const props = withDefaults(defineProps<UIPopoverProps>(), {
    activator: null,
    width: '360px',
    ariaLabel: undefined,
    offset: 8,
    viewportPadding: 8,
})

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<boolean>({ default: false })

const emit = defineEmits<{
    open: []
    close: []
}>()

defineSlots<{
    header?: () => any
    default: () => any
    footer?: () => any
}>()

const activatorEl = computed<HTMLElement | null>(() => (unrefElement(props.activator) ?? null) as HTMLElement | null)
const bounding = useElementBounding(activatorEl)
const { width: windowWidth } = useWindowSize()

const popoverStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = { width: props.width }
    if (activatorEl.value) {
        const widthPx = Number.parseFloat(props.width) || bounding.width.value
        const desiredLeft = bounding.right.value - widthPx
        const maxLeft = windowWidth.value - widthPx - props.viewportPadding
        style.top = `${bounding.bottom.value + props.offset}px`
        style.left = `${Math.min(Math.max(props.viewportPadding, desiredLeft), maxLeft)}px`
    }
    else {
        style.top = '60px'
        style.right = '16px'
    }
    return style
})

const onClickOutsideHandler = {
    handler: () => {
        if (modelValue.value) modelValue.value = false
    },
    options: { ignore: [activatorEl] },
}

onKeyStroke('Escape', () => {
    if (modelValue.value) modelValue.value = false
})

watch(modelValue, (open) => {
    if (open) emit('open')
    else emit('close')
})
</script>

<template>
    <ClientOnly>
        <Teleport to="#teleports">
            <Transition name="ui-popover">
                <section
                    v-if="modelValue"
                    v-on-click-outside="[onClickOutsideHandler.handler, onClickOutsideHandler.options]"
                    class="ui-popover"
                    :class="$attrs.class"
                    :style="popoverStyle"
                    role="dialog"
                    aria-modal="false"
                    :aria-label="props.ariaLabel"
                >
                    <header v-if="$slots.header" class="ui-popover__header">
                        <slot name="header" />
                    </header>

                    <div class="ui-popover__body">
                        <slot />
                    </div>

                    <footer v-if="$slots.footer" class="ui-popover__footer">
                        <slot name="footer" />
                    </footer>
                </section>
            </Transition>
        </Teleport>
    </ClientOnly>
</template>

<style lang="scss" scoped>
.ui-popover {
    --ui-popover-bg: var(--surface, #fff);
    --ui-popover-color: var(--on-surface, #111);

    position: fixed;
    z-index: z(site-nav);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    max-height: calc(100dvh - 80px);
    background: var(--ui-popover-bg);
    color: var(--ui-popover-color);
    border-radius: var(--border-radius, $border-radius);
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.12),
        0 2px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    outline: none;
    pointer-events: auto;

    &__header {
        min-height: fit-content;
    }

    &__body {
        overflow-y: auto;
        min-height: 0;
        -webkit-overflow-scrolling: touch;
    }

    &__footer {
        min-height: fit-content;
    }
}

.ui-popover-enter-active,
.ui-popover-leave-active {
    transition:
        opacity 0.15s ease-out,
        transform 0.15s ease-out;
}

.ui-popover-enter-from,
.ui-popover-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
